import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";

interface Dealer {
  id: string;
  dealer_code: string;
  dealer_name: string;
  is_active: boolean;
  has_accounting: boolean;
  has_e_archive: boolean;
  has_e_invoice: boolean;
  has_restaurant: boolean;
  has_restaurant_stock: boolean;
  has_restaurant_branches: boolean;
  has_restaurant_reports: boolean;
  has_warehouse: boolean;
  created_at: string;
}

export default function AdminDealersPage() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Dealer | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    dealerCode: "", dealerName: "", password: "", isActive: true,
    hasAccounting: false, hasEArchive: false, hasEInvoice: false, hasRestaurant: false,
    hasRestaurantStock: false, hasRestaurantBranches: false, hasRestaurantReports: false, hasWarehouse: false,
  });

  const fetchDealers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("dealers")
        .select("id, dealer_code, dealer_name, is_active, has_accounting, has_e_archive, has_e_invoice, has_restaurant, has_restaurant_stock, has_restaurant_branches, has_restaurant_reports, has_warehouse, created_at")
        .order("created_at", { ascending: false });

      if (err) throw err;
      setDealers(data || []);
    } catch (e: any) {
      setError(e.message || "Bayiler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  const openAdd = () => {
    setEditing(null);
    setForm({ dealerCode: "", dealerName: "", password: "", isActive: true, hasAccounting: false, hasEArchive: false, hasEInvoice: false, hasRestaurant: false, hasRestaurantStock: false, hasRestaurantBranches: false, hasRestaurantReports: false, hasWarehouse: false });
    setShowModal(true);
  };

  const openEdit = (d: Dealer) => {
    setEditing(d);
    setForm({ dealerCode: d.dealer_code, dealerName: d.dealer_name, password: "", isActive: d.is_active, hasAccounting: d.has_accounting, hasEArchive: d.has_e_archive, hasEInvoice: d.has_e_invoice, hasRestaurant: d.has_restaurant, hasRestaurantStock: d.has_restaurant_stock, hasRestaurantBranches: d.has_restaurant_branches, hasRestaurantReports: d.has_restaurant_reports, hasWarehouse: d.has_warehouse ?? false });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.dealerCode.trim() || !form.dealerName.trim()) return;
    if (!editing && !form.password.trim()) return;

    setSaving(true);
    try {
      const supabase = getSupabase();

      if (editing) {
        const updateData: Record<string, any> = {
          dealer_code: form.dealerCode.trim(),
          dealer_name: form.dealerName.trim(),
          is_active: form.isActive,
          has_accounting: form.hasAccounting,
          has_e_archive: form.hasEArchive,
          has_e_invoice: form.hasEInvoice,
          has_restaurant: form.hasRestaurant,
          has_restaurant_stock: form.hasRestaurantStock,
          has_restaurant_branches: form.hasRestaurantBranches,
          has_restaurant_reports: form.hasRestaurantReports,
          has_warehouse: form.hasWarehouse,
        };
        if (form.password.trim()) {
          updateData.password_hash = form.password;
        }

        const { error: err } = await supabase
          .from("dealers")
          .update(updateData)
          .eq("id", editing.id);

        if (err) throw err;
      } else {
        const { error: err } = await supabase
          .from("dealers")
          .insert({
            dealer_code: form.dealerCode.trim(),
            dealer_name: form.dealerName.trim(),
            password_hash: form.password,
            is_active: form.isActive,
            has_accounting: form.hasAccounting,
            has_e_archive: form.hasEArchive,
            has_e_invoice: form.hasEInvoice,
            has_restaurant: form.hasRestaurant,
            has_restaurant_stock: form.hasRestaurantStock,
            has_restaurant_branches: form.hasRestaurantBranches,
            has_restaurant_reports: form.hasRestaurantReports,
            has_warehouse: form.hasWarehouse,
          });

        if (err) throw err;
      }

      setShowModal(false);
      await fetchDealers();
    } catch (e: any) {
      alert(e.message || "Kaydetme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d: Dealer) => {
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase
        .from("dealers")
        .update({ is_active: !d.is_active })
        .eq("id", d.id);

      if (err) throw err;
      await fetchDealers();
    } catch (e: any) {
      alert(e.message || "Durum güncellenirken hata oluştu.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="admin-dealers">Bayi Yönetimi</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Tüm bayileri yönetin, modül yetkilerini düzenleyin</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Bayi Ekle
        </button>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchDealers} className="ml-auto text-accent-700 font-medium cursor-pointer">Tekrar Dene</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          {/* Desktop Table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm min-w-[750px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Bayi Kodu</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Bayi Adı</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Durum</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Muhasebe</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">e-Arşiv</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">e-Fatura</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Restoran</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Stok</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Şube</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Rapor</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {dealers.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-10 text-center text-foreground-400 text-sm">
                      <i className="ri-store-2-line text-3xl block mb-2"></i>
                      Henüz bayi eklenmemiş.
                    </td>
                  </tr>
                ) : (
                  dealers.map((d) => (
                    <tr key={d.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-900">{d.dealer_code}</td>
                      <td className="px-4 py-3 text-foreground-900">{d.dealer_name}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleActive(d)} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${d.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                          {d.is_active ? "Aktif" : "Pasif"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">{d.has_accounting ? <i className="ri-check-line text-primary-600"></i> : <i className="ri-close-line text-foreground-300"></i>}</td>
                      <td className="px-4 py-3 text-center">{d.has_e_archive ? <i className="ri-check-line text-primary-600"></i> : <i className="ri-close-line text-foreground-300"></i>}</td>
                      <td className="px-4 py-3 text-center">{d.has_e_invoice ? <i className="ri-check-line text-primary-600"></i> : <i className="ri-close-line text-foreground-300"></i>}</td>
                      <td className="px-4 py-3 text-center">{d.has_restaurant ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>}</td>
                      <td className="px-4 py-3 text-center">{d.has_restaurant_stock ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>}</td>
                      <td className="px-4 py-3 text-center">{d.has_restaurant_branches ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>}</td>
                      <td className="px-4 py-3 text-center">{d.has_restaurant_reports ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{new Date(d.created_at).toLocaleDateString("tr-TR")}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openEdit(d)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-background-200/70">
            {dealers.length === 0 ? (
              <div className="px-4 py-10 text-center text-foreground-400 text-sm">
                <i className="ri-store-2-line text-3xl block mb-2"></i>
                Henüz bayi eklenmemiş.
              </div>
            ) : (
              dealers.map((d) => (
                <div key={d.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-foreground-900 text-sm">{d.dealer_name}</p>
                      <p className="text-xs text-foreground-400 font-mono">{d.dealer_code}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleActive(d)} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${d.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                        {d.is_active ? "Aktif" : "Pasif"}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                    <span className="flex items-center gap-1">{d.has_accounting ? <i className="ri-check-line text-primary-600"></i> : <i className="ri-close-line text-foreground-300"></i>} Muhasebe</span>
                    <span className="flex items-center gap-1">{d.has_e_archive ? <i className="ri-check-line text-primary-600"></i> : <i className="ri-close-line text-foreground-300"></i>} e-Arşiv</span>
                    <span className="flex items-center gap-1">{d.has_e_invoice ? <i className="ri-check-line text-primary-600"></i> : <i className="ri-close-line text-foreground-300"></i>} e-Fatura</span>
                    <span className="flex items-center gap-1">{d.has_restaurant ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>} Restoran</span>
                    <span className="flex items-center gap-1">{d.has_restaurant_stock ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>} Stok</span>
                    <span className="flex items-center gap-1">{d.has_restaurant_branches ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>} Şube</span>
                    <span className="flex items-center gap-1">{d.has_restaurant_reports ? <i className="ri-check-line text-secondary-600"></i> : <i className="ri-close-line text-foreground-300"></i>} Rapor</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{new Date(d.created_at).toLocaleDateString("tr-TR")}</span>
                    <button onClick={() => openEdit(d)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer active:scale-[0.98]"><i className="ri-edit-line text-sm"></i></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="dealer-form">{editing ? "Bayi Düzenle" : "Yeni Bayi"}</a></h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Bayi Kodu *</label><input type="text" value={form.dealerCode} onChange={(e) => setForm({ ...form, dealerCode: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Bayi Adı *</label><input type="text" value={form.dealerName} onChange={(e) => setForm({ ...form, dealerName: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" /></div>
              <div className="col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1">{editing ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Şifre *"}</label><input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" /></div>
              <div className="col-span-2 border-t border-background-200/70 pt-3 mt-1"><p className="text-xs font-medium text-foreground-500 mb-2">Modül Yetkileri</p></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="acct" checked={form.hasAccounting} onChange={(e) => setForm({ ...form, hasAccounting: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-accent-500 focus:ring-accent-400" />
                <label htmlFor="acct" className="text-sm text-foreground-700">Muhasebe</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="earch" checked={form.hasEArchive} onChange={(e) => setForm({ ...form, hasEArchive: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-accent-500 focus:ring-accent-400" />
                <label htmlFor="earch" className="text-sm text-foreground-700">e-Arşiv</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="einv" checked={form.hasEInvoice} onChange={(e) => setForm({ ...form, hasEInvoice: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-accent-500 focus:ring-accent-400" />
                <label htmlFor="einv" className="text-sm text-foreground-700">e-Fatura</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="rest" checked={form.hasRestaurant} onChange={(e) => setForm({ ...form, hasRestaurant: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-secondary-500 focus:ring-secondary-400" />
                <label htmlFor="rest" className="text-sm text-foreground-700">Restoran Modülü</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="rest_stock" checked={form.hasRestaurantStock} onChange={(e) => setForm({ ...form, hasRestaurantStock: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-secondary-500 focus:ring-secondary-400" />
                <label htmlFor="rest_stock" className="text-sm text-foreground-700">Restoran Stok</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="rest_branch" checked={form.hasRestaurantBranches} onChange={(e) => setForm({ ...form, hasRestaurantBranches: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-secondary-500 focus:ring-secondary-400" />
                <label htmlFor="rest_branch" className="text-sm text-foreground-700">Restoran Şubeler</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="rest_report" checked={form.hasRestaurantReports} onChange={(e) => setForm({ ...form, hasRestaurantReports: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-secondary-500 focus:ring-secondary-400" />
                <label htmlFor="rest_report" className="text-sm text-foreground-700">Restoran Raporlar</label>
                <input type="checkbox" id="warehouse" checked={form.hasWarehouse} onChange={(e) => setForm({ ...form, hasWarehouse: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-primary-500 focus:ring-primary-400" />
                <label htmlFor="warehouse" className="text-sm text-foreground-700 font-medium">🏭 Depo Modülü</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="active" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-accent-500 focus:ring-accent-400" />
                <label htmlFor="active" className="text-sm text-foreground-700">Aktif</label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}